import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, SearchIcon, CheckIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
export interface Option {
  value: string;
  label: string;
  secondary?: string;
  description?: string;
}
interface Props {
  options: Option[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  id?: string;
  ariaLabel?: string;
}
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  id,
  ariaLabel
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = options.find(o => o.value === value) ?? null;
  const filtered = options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()) || (o.secondary ?? '').toLowerCase().includes(query.toLowerCase()));
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);
  return <div ref={rootRef} className="relative" data-id="element-274" data-mp="gie32ozji914">
      <button type="button" id={id} aria-label={ariaLabel} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(o => !o)} className={cn('flex w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2.5 text-left text-sm transition-colors', 'border-gray-300 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-salesforce-500/40 focus:border-salesforce-500')} data-id="element-275" data-mp="gkqml6ky4rga">
        <span className="min-w-0 flex-1" data-id="element-276" data-mp="1d6k4l3160lb6">
          {selected ? <span className="flex flex-col" data-id="element-277" data-mp="198w6jr46ecf2">
              <span className="truncate font-medium text-gray-900" data-id="element-278" data-mp="1gajb0vx5itot">
                {selected.label}
              </span>
              {selected.secondary && <span className="truncate text-xs text-gray-400" data-id="element-279" data-mp="1l9vud6otdimn">
                  {selected.secondary}
                </span>}
            </span> : <span className="text-gray-400" data-id="element-280" data-mp="1v0gyf9n9bfcy">{placeholder}</span>}
        </span>
        <ChevronDownIcon className={cn('h-4 w-4 shrink-0 text-gray-400 transition-transform', open && 'rotate-180')} data-id="element-281" data-mp="1z1slb7eqxmhg" />
      </button>

      {open && <div className="absolute z-30 mt-1.5 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-card-lg" data-id="element-282" data-mp="29087qx2tw7km">
          <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2" data-id="element-283" data-mp="2dxi5r14a9rk2">
            <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-284" data-mp="emqkzca8zeqn" />
            <input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder={searchPlaceholder} className="w-full bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none" data-id="element-285" data-mp="jjqtdtukktkx" />
          </div>
          <ul role="listbox" className="thin-scroll max-h-60 overflow-y-auto py-1" data-id="element-286" data-mp="2o39prvf4eifi">
            {filtered.length === 0 && <li className="px-3 py-4 text-center text-sm text-gray-400" data-id="element-287" data-mp="3ft4fvuasyc58">
                No matches found
              </li>}
            {filtered.map(o => {
          const isSel = o.value === value;
          return <li key={o.value} role="option" aria-selected={isSel} data-id="element-288" data-mp="36resr0nydzd9">
                  <button type="button" onClick={() => {
              onChange(o.value);
              setOpen(false);
              setQuery('');
            }} className={cn('flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50', isSel && 'bg-salesforce-50')} data-id="element-289" data-mp="31uejkgvt4ghx">
                    <span className="min-w-0 flex-1" data-id="element-290" data-mp="3j3p9p8xmhf5j">
                      <span className="flex items-center gap-2" data-id="element-291" data-mp="35br4vaay25f6">
                        <span className="truncate font-medium text-gray-900" data-id="element-292" data-mp="3a9aroylpjqiq">
                          {o.label}
                        </span>
                        {o.secondary && <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-500" data-id="element-293" data-mp="5vcgs0gcbel9">
                            {o.secondary}
                          </span>}
                      </span>
                      {o.description && <span className="mt-0.5 block truncate text-xs text-gray-400" data-id="element-294" data-mp="2clscb4xjjq5r">
                          {o.description}
                        </span>}
                    </span>
                    {isSel && <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-salesforce-600" data-id="element-295" data-mp="2a01df6ipaeqt" />}
                  </button>
                </li>;
        })}
          </ul>
        </div>}
    </div>;
}